import { MaskLine, Rise } from "@/components/motion/Reveal";
import { MATTERS } from "@/data/site";
import { useLang } from "@/i18n/LangContext";
import { ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import Seo from "@/seo/Seo";
import { ldBreadcrumb } from "@/seo/seoConfig";

const BG_IMG = "https://images.unsplash.com/photo-1546412414-e1885259563a?auto=format&fit=crop&w=1800&q=85";

export default function MattersPage() {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-10%", "10%"]);

  return (
    <div className="pt-40">
      <Seo
        title="Selected Matters · Web3, AI, Fundraising, Cyber Response, India Entry"
        description="A discreet selection of RPLC UAE matters — VARA licence-readiness, EU AI Act governance, Series-A structuring, ransomware response, Fractional GC and India-entry corporate structuring."
        path="/matters"
        keywords={["VARA VASP licensing case study", "EU AI Act UAE", "cross-border M&A UAE India", "ransomware response Gulf", "Fractional General Counsel Dubai"]}
        jsonLd={ldBreadcrumb([{ name: "Home", path: "/" }, { name: "Matters", path: "/matters" }])}
      />
      <section ref={ref} className="max-w-[1600px] mx-auto px-6 lg:px-12 relative">
        <Rise><div className="flex items-center gap-4 mb-10"><span className="accent-bar" /><span className="eyebrow">{t.matters.eyebrow}</span></div></Rise>
        <h1 className="font-serif tracking-tight leading-[0.9] text-[13vw] lg:text-[8vw]">
          <MaskLine delay={0.15}>{t.matters.title1}</MaskLine>
          <MaskLine delay={0.32}><span className="italic-accent">{t.matters.title2}</span></MaskLine>
        </h1>
        <Rise delay={0.5}>
          <p className="mt-10 text-[#A19D94] text-lg max-w-2xl leading-relaxed">{t.matters.lead}</p>
        </Rise>

        <motion.div style={{ y }} className="hidden lg:block absolute -right-8 top-24 w-[360px] h-[500px] overflow-hidden border border-[#141414] pointer-events-none">
          <img src={BG_IMG} alt="Chambers" className="w-full h-full object-cover photo-mono" />
          <div className="absolute inset-0 spotlight" />
        </motion.div>
      </section>

      <section className="mt-32 border-y border-[#141414]">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
          {MATTERS.map((m, i) => (
            <Rise key={m.n} delay={i * 0.04}>
              <Link to="/contact" className="pa-row grid grid-cols-12 gap-4 lg:gap-6 items-start py-12 border-b border-[#141414] last:border-0 group cursor-pointer">
                <div className="col-span-2 lg:col-span-1 chapter-num pt-2">{m.n}</div>
                <div className="col-span-10 lg:col-span-2">
                  <div className="chapter-num text-[#66635C]">{m.year}</div>
                  <div className="mt-3 chapter-num text-[#C84C35]">{m.practice}</div>
                </div>
                <div className="col-span-12 lg:col-span-6">
                  <h3 className="pa-title font-serif text-3xl lg:text-5xl leading-[1] tracking-tight">{m.title}</h3>
                  <p className="mt-4 text-[#A19D94] text-base lg:text-lg leading-relaxed max-w-2xl">{m.summary}</p>
                </div>
                <div className="col-span-8 lg:col-span-2 flex items-end lg:justify-end">
                  <div className="font-serif text-3xl lg:text-4xl italic-accent text-[#F4F1EB]">{m.value}</div>
                </div>
                <div className="col-span-4 lg:col-span-1 flex lg:justify-end text-[#66635C] group-hover:text-[#C84C35] transition-colors">
                  <ArrowUpRight size={22} strokeWidth={1.25} />
                </div>
              </Link>
            </Rise>
          ))}
        </div>
      </section>

      <section className="py-32 lg:py-48">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-8">
            <Rise>
              <h2 className="font-serif text-5xl lg:text-[7vw] leading-[0.95] tracking-tight">
                {t.contactCTA.title1} <span className="italic-accent">{t.contactCTA.title2}</span>
              </h2>
            </Rise>
          </div>
          <div className="lg:col-span-4 lg:justify-end flex">
            <Rise delay={0.1}><Link to="/contact" className="btn-primary">{t.contactCTA.cta} <ArrowUpRight size={14} strokeWidth={1.5} /></Link></Rise>
          </div>
        </div>
      </section>
    </div>
  );
}
